"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { EASE } from "@/lib/motion";
import { Icon, type IconName } from "@/components/ui/Icon";
import { PlainSectionHeader } from "./GameSections";
import { RailArrows, useRail } from "./Rail";

export type CategoryCard = {
  id: string;
  slug: string;
  name: string;
  icon: IconName;
  /* How many games the category holds, when known. */
  total: number | null;
};

/* The kit's category cards: a card per category with its icon, name and
   game count, opening the category's page. Two columns that wrap on a phone,
   one rail with chevrons on a desktop — as the kit draws each. */
export function CategoryCards({ title = "Categories", cards }: { title?: string; cards: CategoryCard[] }) {
  const rail = useRail();

  if (cards.length === 0) return null;

  return (
    <section className="flex flex-col gap-[16px]">
      <PlainSectionHeader icon="grid" title={title}>
        <Link
          href="/c/all"
          className="inline-flex h-[32px] items-center rounded-rounded bg-elevation-2 px-[16px] text-[12px] font-bold leading-[1.6] text-fg-subtle transition-colors hover:bg-elevation-3 hover:text-fg"
        >
          View All
        </Link>
        <RailArrows state={rail.state} scrollBy={rail.scrollBy} size="sm" className="hidden lg:flex" />
      </PlainSectionHeader>

      <div
        ref={rail.ref}
        onScroll={rail.measure}
        className="grid grid-cols-2 gap-[8px] lg:scrollbar-none lg:flex lg:snap-x lg:gap-[10px] lg:overflow-x-auto lg:overflow-y-hidden lg:scroll-smooth"
      >
        {cards.map((card, index) => (
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, ease: EASE, delay: Math.min(index * 0.04, 0.3) }}
            className="min-w-0 snap-start lg:w-[174px] lg:shrink-0"
          >
            <Link
              href={`/c/${card.slug}`}
              className="flex h-[75px] items-center gap-[12px] rounded-xl bg-elevation-1 px-[16px] transition-colors duration-200 hover:bg-elevation-2"
            >
              <Icon name={card.icon} size={24} className="shrink-0 text-brand-bold" />
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-[14px] font-bold leading-[1.6] text-fg">{card.name}</span>
                {card.total !== null && (
                  <span className="text-[12px] font-medium leading-[1.6] text-fg-subtle">
                    {card.total} {card.total === 1 ? "game" : "games"}
                  </span>
                )}
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
